import { useContext, useEffect, useState } from 'react';
import { DisaggregatedMetric, GeoDetails, GeoTag } from '../services/types';
import useDeepCompareEffect from './useDeepCompareEffect';
import { boundingBox, orgId as defaultOrgId, excludeIds, setBoundingBox, setExcludeIds } from '../utils/dashboardUtils';
import MetricCategoriesContext from '../context/MetricCategoriesContext';
import LayersContext from '../context/LayersContext';
import { indicatorsEnv } from '../utils/environment';

const useFetchData = (currentTab: string) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const { fetchLayersData } = useContext(LayersContext);
    const [indicators, setIndicators] = useState<DisaggregatedMetric[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [orgId, setOrgId] = useState<number | null>(null);
    const [dashboardView, setDashboardView] = useState('');
    const [geoDetails, setGeoDetails] = useState<GeoDetails[]>([]);
    const [geoLod, setGeoLod] = useState('');
    const [geoTag, setGeoTag] = useState<GeoTag | null>(null);
    const [selectionActive, setSelectionActive] = useState<boolean>(false);
    const [lodMissing, setLodMissing] = useState<boolean>(false);

    const geoEncoding = 'delta';
    const isIframe = window.self !== window.top;
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            console.log('Received message:', event.data);

            if (event.data.message === 'update') {
                const { params } = event.data;

                if (params) {
                    setOrgId(params.organization_id || 9);
                    setGeoLod(params.area_view.geo_lod || '');
                    setDashboardView(params.dashboard_view || '');
                    setGeoDetails(params.area_view.geo_details || []);
                    setBoundingBox(params.area_view?.bounding_box || null);
                    setExcludeIds(params.area_view?.exclude_ids || null);
                    setGeoTag(params.area_view.geo_tag);
                    setSelectionActive(params.area_view?.selection_active || false);
                    setLodMissing(params.area_view?.lod_missing || false);

                    // Fetch layer id
                    console.log("orgId local: ", params.organization_id);

                    console.log('bounding box and exclude ids:', boundingBox, excludeIds);
                }
            }
        };

        if (isIframe) {
            window.addEventListener("message", handleMessage);
        } else {
            // Ensure the dashboardView is synced with currentTab
            setDashboardView(currentTab?.toLowerCase());
            setOrgId(defaultOrgId);
        }

        return () => {
            if (isIframe) window.removeEventListener('message', handleMessage);
        };
    }, [currentTab]);

    useEffect(() => {
        // Trigger fetchLayersData whenever boundingBox or excludeIds change
        if (boundingBox || excludeIds) {
            fetchLayersData(defaultOrgId, boundingBox, excludeIds);
        }
        
    }, [boundingBox, excludeIds]);

    useDeepCompareEffect(() => {
        const fetchData = async () => {
            console.log('details:', geoDetails, geoLod, orgId, dashboardView, geoTag);

            if (!dashboardView) {
                console.log('Current tab is not selected. Skipping fetch...');
                return;
            }

            if (lodMissing) {
                console.log('Current map selection is missing information in geoDetails. Skipping query and displaying clear selection message...');
                setLoading(false);
                return;
            }

            // Check if dashboardView (in lowercase) is in metricCategories (also converted to lowercase)
            const lowercaseMetricCategories = metricCategories.map(category => category.toLowerCase());
            if (!lowercaseMetricCategories.includes(dashboardView.toLowerCase())) {
                console.log('dashboardView is not in metricCategories. Skipping fetch...');
                return;
            }
            
            try {
                if (orgId !== null) {
                    setLoading(true);
                    // Fetch indicators data for current view
                    const indicatorsResponse = await fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ geoDetails, orgId, dashboardView, geoEncoding, geoTag, allowDefault: true, environment: indicatorsEnv }),
                    });

                    if (!indicatorsResponse.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const indicatorsResult = await indicatorsResponse.json();
                    console.log('indicators result:', indicatorsResult);
                    setIndicators(indicatorsResult);
                }
            } catch (error: any) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [geoDetails, geoLod, orgId, dashboardView, boundingBox, geoTag, metricCategories, lodMissing]);

    return { indicators, loading, error, dashboardView, geoDetails, geoTag, selectionActive, lodMissing };
};

export default useFetchData;